import { Platform } from 'react-native';
import {
    isPurchase,
    isRent,
    isSubscription,
    Product,
    Purchase,
    Rent,
    Subscription,
} from '@24i/nxg-sdk-photon/src';
import { useFeature } from '@24i/nxg-sdk-smartott-shared/src/context/AppSettingsData';
import { usePurchaseData } from '../../context/PurchaseData';

type UseuseExternalAccessValueProps = {
    getExternalAccessValue: (externalAccessValue?: string, platform?: string) => string;
    getOfferId: (selectedItem: Purchase | Rent | Product | Subscription) => string;
    getBackstageIdFromStoreProductId: (
        storeProductId: string,
        platform?: string
    ) => Promise<string>;
};

export const getPlatform = () => {
    if (Platform.isTV && Platform.OS === 'ios') return 'tvos';
    return Platform.OS;
};

export const useExternalAccessValue = (): UseuseExternalAccessValueProps => {
    const appstoreProductIDs = useFeature('appstoreProductIDs');
    const { fetchAvailableSubscriptions } = usePurchaseData();
    const getExternalAccessValue = (externalAccessValue, platform = getPlatform()) => {
        if (!appstoreProductIDs || !appstoreProductIDs?.enabled) {
            return externalAccessValue;
        }
        const productData = appstoreProductIDs[externalAccessValue];

        return productData?.[platform] ?? externalAccessValue;
    };

    const getOfferId = (selectedItem: Purchase | Rent | Product | Subscription) => {
        if (isSubscription(selectedItem) || isRent(selectedItem) || isPurchase(selectedItem)) {
            return getExternalAccessValue(selectedItem.externalAccessValue);
        }
        return '';
    };

    const getBackstageIdFromStoreProductId = async (
        storeProductId: string,
        platform = getPlatform()
    ) => {
        const subscriptions = await fetchAvailableSubscriptions();
        if (!subscriptions) {
            return storeProductId;
        }

        if (!appstoreProductIDs || !appstoreProductIDs?.enabled) {
            return storeProductId;
        }

        const externalAccessValue = Object.keys(appstoreProductIDs).reduce<string | null>(
            (acc, key) => {
                const productData = appstoreProductIDs[key];
                if (productData && productData[platform] === storeProductId) {
                    return key;
                }
                return acc;
            },
            null
        );

        if (!externalAccessValue) {
            return storeProductId;
        }

        const finalSubscription = subscriptions?.subscriptions.find(
            (subscription) => subscription.externalAccessValue === externalAccessValue
        );

        return finalSubscription?.id ?? storeProductId;
    };

    return { getExternalAccessValue, getOfferId, getBackstageIdFromStoreProductId };
};
